import {Link, Text, UserContext, VStack} from "@hosttools/frontend";
import {buildChannel} from "@hosttools/frontend/models/channel";
import type React from "react";
import type {ReactElement} from "react";
import {memo, useCallback, useContext} from "react";
import {useHistory} from "react-router-dom";

import Banner from "../Banner";

// import ModalMessage from "./ModalMessage";

const Errors: React.FC = () => {
    const history = useHistory();
    const {errors, user} = useContext(UserContext);

    const isSubUser = !!user.originUserID;

    const gotoChannels = useCallback(() => {
        history.push("/settings/channels");
    }, [history]);

    const gotoSmartLocks = useCallback(() => {
        history.push("/settings/smart-locks");
    }, [history]);

    const gotoListings = useCallback(() => {
        history.push("/settings/listings");
    }, [history]);

    let bannerRenderer: React.ReactElement | null = null;
    let errorElements: React.ReactElement[] = [];

    if (errors.userNotActive) {
        bannerRenderer = (
            <Banner actionType="error">
                <Text color="red.600" variant="sm">
                    Subscribe Today!{" "}
                </Text>
                Your account is no longer active which means that all Host Tools automation has
                stopped for your listings. Please visit the&nbsp;
                <Link className="font-bold" to="/settings/billing">
                    billing page
                </Link>
                &nbsp;and add your credit card to subscribe and re-activate your account.
            </Banner>
        );
    }

    // if (errors.userNotActive && errors.accountAddedAfterTrial) {
    //     bannerRenderer = (
    //         <Banner alertClass="alert-danger">
    //             <p className="card-text">
    //                 <strong>Your data will download as soon as you have subscribed.&nbsp;</strong>
    //                 You added an account after your trial has finished. Please subscribe and Host
    //                 Tools will immediately download your your data.
    //             </p>
    //         </Banner>
    //     );
    // }

    if (!errors.userNotActive && (errors.noActiveAccounts || errors.nonActiveAccounts)) {
        bannerRenderer = (
            <Banner action={gotoChannels} actionText="View Channels" actionType="error">
                <Text color="red.600" variant="sm">
                    Your account is disconnected.{" "}
                </Text>
                Your account needs to be connected to Host Tools. Please visit the channels settings
                page and reconnect your account.
            </Banner>
        );
    }

    if (
        !errors.userNotActive &&
        errors.hasDisconnectedLockAccount &&
        !errors.hasDisconnectedProviderAccounts
    ) {
        bannerRenderer = (
            <Banner action={gotoSmartLocks} actionText="View Smart Locks" actionType="error">
                <Text color="red.600" variant="sm">
                    Your smart lock account is disconnected.{" "}
                </Text>
                Please visit the smart lock settings page and reconnect your account.
            </Banner>
        );
    }

    if (
        !errors.userNotActive &&
        !errors.noAccounts &&
        !errors.noActiveAccounts &&
        errors.noListings
    ) {
        bannerRenderer = (
            <Banner action={gotoListings} actionText="Listings" actionType="warning">
                <Text color="warning.600" variant="sm">
                    No listings found!{" "}
                </Text>
                Host Tools didn&apos;t find any listings associated with your account. If
                you&apos;re account was recently added it can take a few minutes for Host Tools to
                download your listings. Just wait a few minutes and then hit the refresh button.
                <br />
                If you recently added a new listing, go to the&nbsp;
                <Link className="font-bold" to="/settings/listings">
                    settings page
                </Link>
                &nbsp;on Host Tools and click the &quot;Refresh&quot; button next to your account to
                download your latest listings.
            </Banner>
        );
    }

    if (
        !errors.userNotActive &&
        !errors.noActiveAccounts &&
        // !errors.listingsUpdating &&
        errors.noActiveListings
    ) {
        bannerRenderer = (
            <Banner action={gotoListings} actionText="Listing Settings" actionType="info">
                Your listings are currently disabled. When you are ready to go live, please enable
                your listings from the&nbsp;
                <Link className="font-bold" to="/settings/listings">
                    Listing Settings
                </Link>
                &nbsp;page.
                <br />
                <Link
                    href="https://help.hosttools.com/en/articles/7061829-enabled-disabled-and-hidden-what-is-the-difference"
                    type="external"
                >
                    Read more.
                </Link>
            </Banner>
        );
    }

    if (
        !errors.userNotActive &&
        !errors.noActiveAccounts &&
        errors.noVisibleListings &&
        isSubUser
    ) {
        bannerRenderer = (
            <Banner actionType="warning">
                This account is associated with a Listing Group that has child listings linked to
                it. Please ask the owner to remove the listings and then add them again to this
                listing group. Doing this will remove and child listings and only link parent
                listings.
            </Banner>
        );
    }

    if (errors.accountsErrors && !errors.userNotActive) {
        errorElements = [
            ...errorElements,
            ...errors.accountsErrors.map(([elem, errors]) => (
                <Banner
                    key={elem._id}
                    action={gotoChannels}
                    actionText="View channel"
                    actionType="error"
                >
                    <Text className="break-words" color="red.600" variant="sm">
                        {buildChannel(elem.type).label} ({elem.username}) has the following issues:
                    </Text>
                    <ul className="my-1 ps-6">
                        {errors.map(([key, error]) => (
                            <li key={key}>{error.error}</li>
                        ))}
                    </ul>
                </Banner>
            ))
        ];
    }
    if (errors.listingsErrors && !errors.noActiveAccounts && !errors.userNotActive) {
        errorElements = [
            ...errorElements,
            ...errors.listingsErrors.reduce((acc, [elem, listingErrors]) => {
                // skip the listings that are connected to disconnected accounts
                const accountIDsWithErrors = (errors.accountsErrors || []).map(
                    ([elem]) => elem._id
                );
                const parsedListingErrors = listingErrors.filter(
                    entry =>
                        !entry[1].accountID || !accountIDsWithErrors.includes(entry[1].accountID)
                );

                if (parsedListingErrors.length) {
                    acc.push(
                        <Banner
                            key={elem._id}
                            action={gotoListings}
                            actionText="View listing"
                            actionType="warning"
                        >
                            {elem.name} has the following issues:
                            <ul className="my-1 ps-6">
                                {parsedListingErrors.map(([key, error]) => (
                                    <li key={key}>{error.error}</li>
                                ))}
                            </ul>
                        </Banner>
                    );
                }
                return acc;
            }, [] as ReactElement[])
        ];
    }

    //
    //
    //
    // Temp banner for beta users we need to talk to
    //
    //
    // const isBetaUserWeNeedToTalkTo = [
    //     // "604f9301b7ef9d23ff3c7798", // testtest
    //     // "5b11dcdd44b20200087bd13d", // neil.aneja@gmail.com
    //     // "5b8745cecdda3300087fcfaa", // vannaguo@gmail.com
    //     // "5ae2377189fab400084846e7", // str@cgstl.com
    //     "5b61ad9eeb39810008e85854" // stefanpauloswald@gmail.com
    //     // "5b6b787699cb7a0008a4c8b6", // jose.g.yapor@gmail.com
    //     // "5b85c0a8c8056c000823c5a3" // gerardi.florian@gmail.com
    // ].includes(user._id);
    // const isBetaUserThatHaveNotEmailed = [
    //     // "604f9301b7ef9d23ff3c7798", // testtest
    //     // "5b11dcdd44b20200087bd13d", // neil.aneja@gmail.com
    //     // "5b8745cecdda3300087fcfaa", // vannaguo@gmail.com
    //     // "5ae2377189fab400084846e7", // str@cgstl.com
    //     "5b61ad9eeb39810008e85854" // stefanpauloswald@gmail.com
    //     // "5b6b787699cb7a0008a4c8b6" // jose.g.yapor@gmail.com
    //     // "5b85c0a8c8056c000823c5a3" // gerardi.florian@gmail.com
    // ].includes(user._id);
    // if (isBetaUserWeNeedToTalkTo) {
    //     bannerRenderer = (
    //         <Banner actionType="error">
    //             <Text color="red.600" variant="sm">
    //                 We need your help!{" "}
    //             </Text>
    //             <br />
    //             Host Tools has been accepted as a Preferred Partner with VRBO/Expedia group and we
    //             need your help.
    //             <br />
    //             <br />
    //             {"Tom sent you an email about joining the VRBO Pilot Program. "}
    //             <strong>We need your response right away</strong>
    //             <strong>
    //                 , please send Tom an email to
    //                 <a
    //                     href={`mailto:t@hosttools.com?subject=VRBO Pilot Program&body=%0d%0a%0d%0a%0d%0a%0d%0a--------%0d%0aBeta UserID: ${user._id}`}
    //                     rel="noreferrer"
    //                     target="_blank"
    //                 >
    //                     {" t@hosttools.com "}
    //                 </a>
    //                 now.
    //             </strong>
    //             <br />
    //             <br />
    //             We need a response from all Beta users right away.
    //             <br />
    //             <br />
    //             <strong>
    //                 You are seeing this message because Tom is waiting for your response about the
    //                 VRBO Pilot Program and we need to hear from you.
    //             </strong>
    //         </Banner>
    //     );
    // }

    return (
        <VStack className="min-h-[auto]" testID="error-container">
            {bannerRenderer}
            {errorElements.length > 0 && errorElements}
            {/* {isBetaUserWeNeedToTalkTo && (
                    <ModalMessage hideCloseButtons={isBetaUserThatHaveNotEmailed} user={user} />
                )} */}
        </VStack>
    );
};

export default memo(Errors);
